import React, { useCallback, useState } from 'react';
import Modal from '@decisiv/ui-components/lib/components/Modal';
import { P } from '@decisiv/ui-components';

import { t, Trans } from '@lingui/macro';
import PropTypes from 'prop-types';
import { Button, Flex } from '@decisiv/ui-components/lib/components';
import iconix from '@decisiv/iconix';
import IntegrationsApi from '../../../api/integrations';
import WrapperWithLoading from '../../../components/WrapperWithLoading';
import { formatWeekdayDateTime } from '../../../utils/dates';

export default function ExportLocationsModal({ integration = {}, ...props }) {
  const [isJobRunning, setIsJobRunning] = useState(false);
  const [fileReady, setFileReady] = useState(false);
  const [fileAttributes, setFileAttributes] = useState({});

  const handleCloseModal = () => {
    props.setVisibleExportModal(false);
  };

  const checkExportLocationStatus = useCallback(() => {
    const intervalId = setInterval(async () => {
      try {
        const response = await IntegrationsApi.exportLocations(integration.id);

        if (response) {
          if (response?.data?.status === 'completed') {
            clearInterval(intervalId);
            setIsJobRunning(false);
            setFileAttributes(response.data);
            setFileReady(true);
          } else if (response?.data?.status === 'failed') {
            clearInterval(intervalId);
            setIsJobRunning(false);
          }
        }
      } catch (err) {
        setIsJobRunning(false);
      }
    }, 5000);

    return () => clearInterval(intervalId);
  });

  const handleDownloadExport = async () => {
    const link = document.createElement('a');
    link.href = fileAttributes.file_path;
    link.setAttribute(
      'download',
      `${integration.attributes.name.replace(/ /g, '_')}-export-locations.csv`,
    );
    document.body.appendChild(link);
    link.click();
  };

  // eslint-disable-next-line consistent-return
  const handleExportLocations = async () => {
    setIsJobRunning(true);

    if (fileReady) {
      setIsJobRunning(false);
      return handleDownloadExport();
    }

    const response = await IntegrationsApi.exportLocations(integration.id);

    if (response.status === 200) {
      checkExportLocationStatus();
    } else {
      setIsJobRunning(false);
    }
  };

  const handleForceExport = async (force = true) => {
    setIsJobRunning(true);

    const response = await IntegrationsApi.exportLocations(integration.id, {
      force,
    });

    if (response.status === 200) {
      checkExportLocationStatus();
    } else {
      setIsJobRunning(false);
    }
  };

  return (
    <Modal
      visible={props.visibleExportModal}
      onClose={handleCloseModal}
      title="Export Locations"
      intent="information"
    >
      <WrapperWithLoading
        loading={isJobRunning}
        description={t`Your file is being generated. You can safely close this modal and return later to download it.`}
      >
        {fileReady ? (
          <Flex flexDirection="column" flex={1}>
            <P shade={1} marginY={1}>
              <Trans>
                Download the CSV generated on{' '}
                <b>{formatWeekdayDateTime(fileAttributes?.updated_at)} </b> or
                generate a new export.
              </Trans>
            </P>

            <Flex flex={2} flexDirection="column" margin={1} style={{ gap: 5 }}>
              <Button
                text="Download"
                icon={iconix.Download}
                onClick={() => handleExportLocations()}
              />
              <Button
                text="Generate new Export"
                icon={iconix.Refresh}
                onClick={() => handleForceExport()}
              />
            </Flex>
          </Flex>
        ) : (
          <Flex flexDirection="column" flex={1}>
            <P shade={1} marginY={1}>
              <Trans>
                Click export to generate a CSV file containing all configured
                locations.
              </Trans>
            </P>
            <Button
              text="Export"
              icon={iconix.Download}
              onClick={() => handleExportLocations()}
            />
          </Flex>
        )}
      </WrapperWithLoading>
    </Modal>
  );
}

ExportLocationsModal.propTypes = {
  setVisibleExportModal: PropTypes.func.isRequired,
  visibleExportModal: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  integration: PropTypes.objectOf(PropTypes.any),
};
